<script lang="ts">
    // Svelte/Jet/Models/Types
    import { createEventDispatcher, onDestroy } from 'svelte';
    import { tick } from 'svelte/internal';
    import { getJet, getJetPerform } from '~/jet';
    import type {
        TrackLockupCollection,
        ColumnHeader,
        WorkHeader as WorkHeaderType,
        TrackLockup,
        TrackLockupLayoutKind,
    } from '~/jet/models';
    import type { TrackListColumn } from '@amp/web-app-components/src/components/TrackList/types';

    // Stores
    import { getI18n } from '~/stores/i18n';
    import { getItemSelector } from '~/stores/item-selector';

    // Components
    import WorkHeader from './WorkHeader.svelte';
    import TrackListItem from './TrackListItem.svelte';

    // Shared Components
    import TrackListColumnHeader from '@amp/web-app-components/src/components/TrackList/TrackListColumnHeader.svelte';

    // Utilities
    import { getLocale } from '~/utils/locale';

    // Shared Utilities
    import allowDrag from '@amp/web-app-components/src/actions/allow-drag';
    import { loggerFor } from '@amp/web-apps-logger';

    // Imports not used for now but may be needed/need to be created later (or Music-equivalent thereof)
    //
    // import {
    // isColumnSectionHeader,
    //     isComposerSectionHeader,
    // } from '~/jet/models';
    // import {
    //     type MusicDropData,
    //     DropTarget,
    // } from '~/utils/drag-and-drop/types';
    // import { updateReorderPlaylistAction } from '~/jet/web-actions/reorder-playlist-action';
    // import {
    //     getContentId,
    //     getContentIdForLibraryState,
    //     getContentDescriptorId,
    //     isLibraryDescriptor,
    // } from '~/utils/content-descriptor';
    // import { getCommerce } from '~/utils/commerce';
    // import { getLocalLibraryState } from '~/utils/local-library-state';
    // import { ContentKind } from '@jet-app/music/environment/view-model/content-descriptor/ContentDescriptor';
    // import { LibraryRelationshipOperationType } from '~/jet/intents/library/relationships/types';
    // import { makeLibraryRelationshipsIntent } from '~/jet/intents/library/relationships/LibraryRelationshipsIntent';
    // import { makeInvalidationAction } from '~/jet/web-actions/invalidation-actions';
    // import { makeRemoveQueueItemAction } from '~/jet/web-actions';
    // import { fetchInLibraryStatusForSections } from '~/utils/fetch-in-library';

    const tracklistIdentifier = Symbol();
    // const localLibraryState = getLocalLibraryState();
    // const { state: commerceState } = getCommerce();
    const i18n = getI18n();
    const jet = getJet();
    const perform = getJetPerform();
    const itemSelector = getItemSelector();
    const locale = getLocale();
    const log = loggerFor('<TrackList>');

    export let section: TrackLockupCollection;

    const dispatch = createEventDispatcher();
    let reorderPosition: number | null = null;
    let reorderTimeout = null;

    // $: containerContentDescriptor = section.containerContentDescriptor;
    // $: containerArtwork = section.containerArtwork;
    $: canEdit = !!section.canEdit;
    $: reorderInProgress =
        reorderPosition !== undefined && reorderPosition !== null;
    // $: reorderEnabled =
    //     canEdit &&
    //     jet.isUserInHomeStorefront() &&
    //     !!section.reorderAction &&
    //     !reorderInProgress;
    $: reorderEnabled = false;
    $: tracks = section.items || [];
    $: {
        // When tracks did change
        tracks;
        updateTracks();
    }

    // Only show duration in Work header if more than one track
    $: showWorkDuration = tracks?.length > 1;

    const updateTracks = () => {
        itemSelector.clearSelection();
        itemSelector.addItems(tracklistIdentifier, tracks);
        // // TODO: rdar://106287653 (Onyx Music on the Web: Tracklist: duplicate tracks selection state after reorder is wrong)
        // const oldTracks = itemSelector.getItems(tracklistIdentifier);
        // const oldSelection = $itemSelector.selectedItems.map(
        //     (selectedItem) => ({
        //         index: oldTracks.indexOf(selectedItem),
        //         id: getContentId(selectedItem),
        //     }),
        // );
        // // Reset current items for this tracklist and add the updated list
        // itemSelector.resetItems(tracklistIdentifier);
        // itemSelector.clearSelection();
        // itemSelector.addItems(tracklistIdentifier, tracks);
        // if (oldSelection) {
        //     // Reselect all previously selected tracks
        //     const selectedTracks = oldSelection
        //         .map(({ index, id }) => {
        //             // If the new tracklist has the same track on the same index, select that
        //             if (getContentId(tracks[index]) === id) {
        //                 return tracks[index];
        //             }
        //             // otherwise: select the first instance of the same track (this could be the wrong one, but this is our best guess)
        //             return tracks.find((track) => getContentId(track) === id);
        //         })
        //         .filter(Boolean);
        //     itemSelector.selectMultiple(selectedTracks);
        // }
    };

    // $: containerId =
    //     !!containerContentDescriptor &&
    //     getContentIdForLibraryState({
    //         contentDescriptor: containerContentDescriptor,
    //     });
    // $: containerKind = containerContentDescriptor?.kind;
    // $: containerState = localLibraryState.subscribeToUpdates(containerId);
    // $: containerInLibrary =
    //     $containerState &&
    //     !!containerKind &&
    //     localLibraryState.get(containerId, containerKind)?.inLibrary;
    $: containerInLibrary = false;

    // Used to update status of track list items when the container state has changed.
    // Useful for when adding/removing container from library also adds/removes all
    // of the container children.  Limiting to just Albums for now as adding/removing
    // playlists from the container will not add/remove the playlist tracks.
    $: containerInLibrary, updateTrackListLibraryState();

    function updateTrackListLibraryState() {
        // if (containerKind === ContentKind.Album && !!tracks) {
        //     // Adding a delay here to avoid any race conditions between
        //     // the inLibrary status changes updating and us requesting them
        //     setTimeout(() => {
        //         fetchInLibraryStatusForSections(
        //             [section],
        //             $commerceState.user?.subscriberType,
        //             jet,
        //             log,
        //             locale,
        //             localLibraryState,
        //             true, // noCache to allow fresh updates
        //         );
        //     }, 250);
        // }
    }

    // $: draggedItems = (() => {
    //     if (!$itemSelector.currentMouseTarget) return [];
    //     return $itemSelector.selectedItems.includes(
    //         $itemSelector.currentMouseTarget,
    //     )
    //         ? $itemSelector.selectedItems
    //         : [$itemSelector.currentMouseTarget];
    // })();
    // $: draggedDescriptors = draggedItems.map(
    //     (trackLockup: TrackLockup) => trackLockup?.contentDescriptor,
    // );
    // $: indexedDragDescriptors = draggedItems.map((item: TrackLockup) => ({
    //     index: tracks.indexOf(item),
    //     item: item.contentDescriptor,
    // }));
    // $: containerItemDescriptors = tracks.map((t) => t.contentDescriptor);
    $: header = section.header?.item as WorkHeaderType;

    $: layoutKind = ((): TrackLockupLayoutKind => {
        const layoutStyle = tracks[0]?.layoutStyle;
        return typeof layoutStyle === 'string'
            ? layoutStyle
            : layoutStyle?.kind;
    })();

    $: isAlbum = layoutKind === 'albumTrackList';
    $: isCompilation = layoutKind === 'compilationTrackList';
    $: isPlaylist = layoutKind === 'playlistTrackList';
    $: isRecording = layoutKind === 'recordingTrackList';

    // $: shouldShowRemoveFromPlaylist =
    //     (isLibraryDescriptor(section.containerContentDescriptor) &&
    //         isPlaylist &&
    //         canEdit &&
    //         jet.isUserInHomeStorefront()) ??
    //     false;

    // $: hasColumnHeader = isColumnSectionHeader(section.header);
    // $: hasComposerHeader = isComposerSectionHeader(section.header);
    $: hasColumnHeader = !section.header;
    $: hasWorkHeader = !!section.header;

    $: columns = [
        { headerText: '', key: 'prefix' },
        {
            key: 'song',
            headerText: $i18n.t(
                'ASE.Web.MusicClassical.TracksListColumnHeaders.track',
            ),
        }, // song (or video when all items are video)
        {
            headerText: $i18n.t(
                'ASE.Web.MusicClassical.TracksListColumnHeaders.time',
            ),
            key: 'time',
        },
    ];

    // TODO: Add Drag and Drop support
    //       rdar://137428544 (Web Classical Music: Album/Playlist Detail Pages - Drag and Drop Support)
    // async function dropOnTrack(
    //     _track: TrackLockup,
    //     dropIndex: number,
    //     { detail }: CustomEvent<MusicDropData>,
    // ): Promise<void> {
    //     if (
    //         reorderEnabled &&
    //         section.reorderAction &&
    //         'indexedItems' in detail.data &&
    //         getContentDescriptorId(detail.data.containerDescriptor) ===
    //             getContentDescriptorId(containerContentDescriptor)
    //     ) {
    //         let afterIndex =
    //             detail.dropTarget === DropTarget.Top
    //                 ? dropIndex - 1
    //                 : dropIndex;

    //         setReorderPosition(afterIndex);
    //         await perform(
    //             updateReorderPlaylistAction(
    //                 section.reorderAction,
    //                 afterIndex,
    //                 detail.data,
    //             ),
    //         );
    //         resetReorderPosition();
    //     }
    //     return;
    // }

    function setReorderPosition(position: number) {
        reorderPosition = position;
    }

    async function resetReorderPosition() {
        clearTimeout(reorderTimeout);
        reorderPosition = null;
        reorderTimeout = null;

        await tick();
    }

    const handleKeydown = (event: KeyboardEvent): void => {
        if (event.key === 'a' && (event.metaKey || event.ctrlKey)) {
            event.preventDefault();
            // TODO: prevent scrolling to the bottom of the page
            itemSelector.selectAll();
        }
    };

    onDestroy(() => {
        itemSelector.resetItems(tracklistIdentifier);
        // localLibraryState.unsubscribeFromUpdates(containerId);
    });
</script>

<!-- TODO: rdar://89020012 (Web Music Jet: Track list infinite scroll) -->
<!-- TODO: selection + subscribeClickOutside / unsubscribeClickOutside -->
<!-- TODO: rdar://89001130 (JMOTW: Add Tracklist classes for Works) -->
<!-- TODO: rdar://89001159 (JMOTW: Add Tracklist classes for Discs) -->
<!-- TODO: rdar://89001065 (JMOTW: Add `outerClass` to Tracklist) -->

<svelte:window on:keydown={handleKeydown} />

<!-- svelte-ignore a11y-interactive-supports-focus -->
<!-- TODO: Put use:allowDrag back in -->
<!-- rdar://137428544 (Web Classical Music: Album/Playlist Detail Pages - Drag and Drop Support) -->
<div
    class="songs-list"
    class:songs-list--has-works-header={hasWorkHeader}
    class:songs-list--album={isAlbum}
    class:songs-list--playlist={isPlaylist}
    class:songs-list--compilation={isCompilation}
    class:songs-list--recording={isRecording}
    on:keydown={itemSelector.handleKeydown}
    role="grid"
    data-testid="tracklist"
>
    {#if hasWorkHeader}
        <WorkHeader {header} {showWorkDuration} />
    {:else}
        <!-- Always needs header columns for AX, even when the Jet data doesn't give us -->
        <TrackListColumnHeader {columns} isVisible={isPlaylist} />
    {/if}

    {#each tracks as item, rowIndex}
        {#if rowIndex === 0 && reorderInProgress && reorderPosition === -1}
            <div class="reorder-placeholder" />
        {/if}

        <!-- TODO: Put back on:removeFromPlaylist and on:drop -->
        <!-- rdar://137428544 (Web Classical Music: Album/Playlist Detail Pages - Drag and Drop Support) -->
        <!-- rdar://137428365 (Web Classical Music: Album/Playlist Detail Pages - Track/Work Library States) -->
        <TrackListItem
            {item}
            {rowIndex}
            showByline={true}
            showSecondColumn={false}
            showThirdColumn={false}
            showFourthColumn={false}
            hasReactions={false}
            isFirst={rowIndex === 0}
            isLast={rowIndex === tracks.length - 1}
            dropEnabled={reorderEnabled && !reorderInProgress}
            isSelected={$itemSelector.selectedItems.includes(item)}
            isLastSelected={$itemSelector.selectedItems[
                $itemSelector.selectedItems.length - 1
            ] === item}
            on:contextMenuActionComplete={(event) => {
                dispatch('catalogActionOnTrackListItem', event.detail);
            }}
        />

        {#if reorderInProgress && reorderPosition === rowIndex}
            <div class="reorder-placeholder" />
        {/if}
    {/each}

    <!-- TODO: rdar://89020012 (Web Music Jet: Track list infinite scroll)  -->
</div>

<style lang="scss">
    @use '@amp/web-shared-styles/sasskit-stylekit/ac-sasskit-config';
    @use 'ac-sasskit/core/selectors' as *;
    @use 'ac-sasskit/core/helpers' as *;
    @use 'ac-sasskit/modules/viewportcontent/core' as *;
    @use 'amp/stylekit/core/functions/dynamic-grids' as *;
    @use 'amp/stylekit/core/mixins/browser-targets' as *;
    @use 'amp/stylekit/core/mixins/line-clamp' as *;
    @use '@amp/web-shared-styles/app/core/viewports' as *;
    @use '@amp/web-shared-styles/app/core/globalvars' as *;
    @use '../../../styles/music-classical/core/globalvars' as *;

    // Table layouts are super fragile, so make sure to check:
    // - Albums
    // - Playlists
    // - Classical Playlists
    // - Classical Albums
    // - Top Songs See All room
    // - Library Albums

    $songs-list-header-height: 32px;
    $songs-list-header-col-bottom-spacer: 6px;
    $songs-list-explicit-badge-size: 9px;

    // $web-navigation-width-small: 164px;
    $web-navigation-width-large: $global-sidebar-width-large;

    // $web-content-area-min-width: 340px;

    .songs-list {
        --linkColor: var(--systemSecondary);
        width: calc(100% - var(--bodyGutter));
        display: table;
        border-collapse: collapse;
        // No row gap. Use `border-spacing: 0 1px` if a row gaps are needed
        border-spacing: 0;
        table-layout: fixed; // Gives us more control over column widths
        font: var(--callout);
        margin-inline-end: var(--bodyGutter);

        &.songs-list--has-works-header:hover {
            --rowBackgroundColor: var(--tracklistHoverColor);
            --tracklistAltRowColor: var(--tracklistHoverColor);

            :global(.composer-header) {
                background-color: var(--tracklistWorksTrackHoverColor);
            }
        }

        // Hide add to library and context menu grid items
        // when container is empty
        :global(.songs-list-row__add-to-library:empty),
        :global(.songs-list-row__context-menu:empty) {
            display: none;
        }

        // Override width for duration + more button
        @media (--range-grid-layout-xs-1-sidebar-down) {
            --tracklist-time-col-width: 110px;
        }
    }

    // .songs-list__song-link-wrapper {
    //     @include line-clamp();

    // TODO: rdar://89617374 (JMOTW: Tracklist: LTR and RTL layout)
    //     // Turn off RTL/LTR BIDI override for this element.
    //     &[dir] {
    //         [dir=rtl] & {
    //             // Force `left` and `right` styles for RTL pages
    //             text-align: right;
    //         }

    //         [dir=ltr] & {
    //             // Force `left` and `right` styles for LTR pages
    //             text-align: left;
    //         }
    //     }
    // }

    //
    // Classical / Works
    //
    .songs-list--has-works-header {
        // context button width (28px) + button margin (7px)
        $context-menu-grid-size: 35px;

        // Define contextual menu column width in order to align work header duration
        // with track list duration when contextual menu is unavailable
        :global(.composer-header:has(.songs-list-row__context-menu:empty)) {
            --songs-row-controls-columns: auto auto #{$context-menu-grid-size};
        }

        :global(.songs-list__col--work-header) {
            padding-inline-start: 18px;
        }

        :global(.composer-header__first-row) {
            font: var(--subhead-emphasized);
            text-transform: uppercase;
            color: var(--systemSecondary);
        }

        :global(.songs-list-row__by-line--work) {
            --linkColor: var(--systemPrimary);
            font: var(--body-emphasized);
        }
    }

    // prettier-ignore
    :global(.section--has-composer-header
            + .section--has-composer-header) {
        // Spacer after works
        &::before {
            content: '';
            height: $songs-list-works-spacer;
            display: block;
            line-height: 0;
        }
    }

    .reorder-placeholder {
        display: table-row;
        position: relative;

        &::after {
            width: 100%;
            height: 2px;
            content: '';
            position: absolute;
            display: block;
            animation: reorder-pulse 850ms infinite alternate ease-in;
            animation-delay: 1s;
            right: 0;
            left: 0;
            background-color: var(--keyColor);
            border-radius: 2px;
            z-index: var(--z-default);
        }
    }

    @keyframes reorder-pulse {
        0% {
            background-color: rgba(var(--keyColor-rgb), 1);
        }

        100% {
            background-color: rgba(var(--keyColor-rgb), 0.45);
        }
    }
</style>
